import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './mime-types.component.module.scss';

interface MimeRow {
    mime: string;
    extensions: string[];
    name: string;
    category: 'application' | 'audio' | 'font' | 'image' | 'text' | 'video' | 'multipart' | 'message';
}

const MIME: MimeRow[] = [
    // Application
    { mime: 'application/json', extensions: ['json'], name: 'JSON', category: 'application' },
    { mime: 'application/ld+json', extensions: ['jsonld'], name: 'JSON-LD', category: 'application' },
    { mime: 'application/xml', extensions: ['xml'], name: 'XML', category: 'application' },
    { mime: 'application/javascript', extensions: ['js'], name: 'JavaScript (legacy)', category: 'application' },
    { mime: 'application/pdf', extensions: ['pdf'], name: 'PDF', category: 'application' },
    { mime: 'application/zip', extensions: ['zip'], name: 'ZIP archive', category: 'application' },
    { mime: 'application/gzip', extensions: ['gz'], name: 'GZIP archive', category: 'application' },
    { mime: 'application/x-7z-compressed', extensions: ['7z'], name: '7-Zip archive', category: 'application' },
    { mime: 'application/x-rar-compressed', extensions: ['rar'], name: 'RAR archive', category: 'application' },
    { mime: 'application/x-tar', extensions: ['tar'], name: 'TAR archive', category: 'application' },
    { mime: 'application/octet-stream', extensions: ['bin'], name: 'Arbitrary binary data', category: 'application' },
    { mime: 'application/wasm', extensions: ['wasm'], name: 'WebAssembly module', category: 'application' },
    { mime: 'application/sql', extensions: ['sql'], name: 'SQL', category: 'application' },
    { mime: 'application/vnd.api+json', extensions: [], name: 'JSON:API', category: 'application' },
    { mime: 'application/x-www-form-urlencoded', extensions: [], name: 'URL-encoded form', category: 'application' },
    { mime: 'application/vnd.ms-excel', extensions: ['xls'], name: 'Microsoft Excel (legacy)', category: 'application' },
    { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extensions: ['xlsx'], name: 'Microsoft Excel', category: 'application' },
    { mime: 'application/msword', extensions: ['doc'], name: 'Microsoft Word (legacy)', category: 'application' },
    { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extensions: ['docx'], name: 'Microsoft Word', category: 'application' },
    { mime: 'application/vnd.ms-powerpoint', extensions: ['ppt'], name: 'Microsoft PowerPoint (legacy)', category: 'application' },
    { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', extensions: ['pptx'], name: 'Microsoft PowerPoint', category: 'application' },
    { mime: 'application/epub+zip', extensions: ['epub'], name: 'EPUB ebook', category: 'application' },
    { mime: 'application/rtf', extensions: ['rtf'], name: 'Rich Text Format', category: 'application' },
    { mime: 'application/x-shockwave-flash', extensions: ['swf'], name: 'Flash (deprecated)', category: 'application' },

    // Audio
    { mime: 'audio/mpeg', extensions: ['mp3'], name: 'MP3', category: 'audio' },
    { mime: 'audio/wav', extensions: ['wav'], name: 'WAV', category: 'audio' },
    { mime: 'audio/ogg', extensions: ['ogg', 'oga'], name: 'Ogg Vorbis', category: 'audio' },
    { mime: 'audio/webm', extensions: ['weba'], name: 'WebM audio', category: 'audio' },
    { mime: 'audio/flac', extensions: ['flac'], name: 'FLAC', category: 'audio' },
    { mime: 'audio/aac', extensions: ['aac'], name: 'AAC', category: 'audio' },
    { mime: 'audio/mp4', extensions: ['m4a'], name: 'AAC in MP4', category: 'audio' },
    { mime: 'audio/x-aiff', extensions: ['aiff', 'aif'], name: 'AIFF', category: 'audio' },
    { mime: 'audio/x-ms-wma', extensions: ['wma'], name: 'Windows Media Audio', category: 'audio' },
    { mime: 'audio/midi', extensions: ['mid', 'midi'], name: 'MIDI', category: 'audio' },

    // Font
    { mime: 'font/woff', extensions: ['woff'], name: 'WOFF', category: 'font' },
    { mime: 'font/woff2', extensions: ['woff2'], name: 'WOFF2', category: 'font' },
    { mime: 'font/ttf', extensions: ['ttf'], name: 'TrueType font', category: 'font' },
    { mime: 'font/otf', extensions: ['otf'], name: 'OpenType font', category: 'font' },
    { mime: 'font/collection', extensions: ['ttc'], name: 'TrueType collection', category: 'font' },

    // Image
    { mime: 'image/jpeg', extensions: ['jpg', 'jpeg'], name: 'JPEG', category: 'image' },
    { mime: 'image/png', extensions: ['png'], name: 'PNG', category: 'image' },
    { mime: 'image/gif', extensions: ['gif'], name: 'GIF', category: 'image' },
    { mime: 'image/webp', extensions: ['webp'], name: 'WebP', category: 'image' },
    { mime: 'image/avif', extensions: ['avif'], name: 'AVIF', category: 'image' },
    { mime: 'image/svg+xml', extensions: ['svg'], name: 'SVG', category: 'image' },
    { mime: 'image/x-icon', extensions: ['ico'], name: 'Icon', category: 'image' },
    { mime: 'image/tiff', extensions: ['tif', 'tiff'], name: 'TIFF', category: 'image' },
    { mime: 'image/bmp', extensions: ['bmp'], name: 'Bitmap', category: 'image' },
    { mime: 'image/heic', extensions: ['heic'], name: 'HEIC', category: 'image' },
    { mime: 'image/heif', extensions: ['heif'], name: 'HEIF', category: 'image' },

    // Text
    { mime: 'text/plain', extensions: ['txt'], name: 'Plain text', category: 'text' },
    { mime: 'text/html', extensions: ['html', 'htm'], name: 'HTML', category: 'text' },
    { mime: 'text/css', extensions: ['css'], name: 'CSS', category: 'text' },
    { mime: 'text/javascript', extensions: ['js', 'mjs'], name: 'JavaScript', category: 'text' },
    { mime: 'text/csv', extensions: ['csv'], name: 'CSV', category: 'text' },
    { mime: 'text/markdown', extensions: ['md', 'markdown'], name: 'Markdown', category: 'text' },
    { mime: 'text/xml', extensions: ['xml'], name: 'XML (text variant)', category: 'text' },
    { mime: 'text/calendar', extensions: ['ics'], name: 'iCalendar', category: 'text' },
    { mime: 'text/vcard', extensions: ['vcf'], name: 'vCard', category: 'text' },
    { mime: 'text/event-stream', extensions: [], name: 'Server-Sent Events', category: 'text' },
    { mime: 'text/yaml', extensions: ['yaml', 'yml'], name: 'YAML (de facto)', category: 'text' },

    // Video
    { mime: 'video/mp4', extensions: ['mp4'], name: 'MP4', category: 'video' },
    { mime: 'video/webm', extensions: ['webm'], name: 'WebM', category: 'video' },
    { mime: 'video/ogg', extensions: ['ogv'], name: 'Ogg video', category: 'video' },
    { mime: 'video/quicktime', extensions: ['mov'], name: 'QuickTime', category: 'video' },
    { mime: 'video/x-msvideo', extensions: ['avi'], name: 'AVI', category: 'video' },
    { mime: 'video/mpeg', extensions: ['mpeg', 'mpg'], name: 'MPEG video', category: 'video' },
    { mime: 'video/x-matroska', extensions: ['mkv'], name: 'Matroska', category: 'video' },
    { mime: 'video/x-flv', extensions: ['flv'], name: 'Flash Video', category: 'video' },
    { mime: 'video/3gpp', extensions: ['3gp'], name: '3GPP', category: 'video' },

    // Multipart / message
    { mime: 'multipart/form-data', extensions: [], name: 'Form upload boundary', category: 'multipart' },
    { mime: 'multipart/byteranges', extensions: [], name: 'Byte-range responses', category: 'multipart' },
    { mime: 'message/rfc822', extensions: ['eml'], name: 'Email message', category: 'message' },
];

type Cat = 'all' | MimeRow['category'];

interface State { search: string; category: Cat; }

export default class MimeTypesComponent extends Component<{ feature?: string }, State> {
    state: State = { search: '', category: 'all' };

    private get filtered(): MimeRow[] {
        const q = this.state.search.trim().toLowerCase();
        return MIME.filter(m => {
            if (this.state.category !== 'all' && m.category !== this.state.category) return false;
            if (!q) return true;
            return m.mime.toLowerCase().includes(q)
                || m.name.toLowerCase().includes(q)
                || m.extensions.some(e => e.toLowerCase().includes(q));
        });
    }

    render() {
        const { search, category } = this.state;
        const rows = this.filtered;
        const cats: Cat[] = ['all', 'application', 'audio', 'font', 'image', 'text', 'video', 'multipart', 'message'];
        return (
            <div className="row">
                <div className="col-12">
                    <div className={styles.controls}>
                        <input className={styles.search} type="search"
                            placeholder="Search MIME, extension, or name…"
                            value={search} onChange={e => this.setState({ search: e.target.value })}
                            aria-label="Filter MIME types" />
                        <div className={styles.tabs} role="tablist">
                            {cats.map(c => (
                                <button key={c} role="tab"
                                    className={`${styles.tab} ${category === c ? styles.tabActive : ''}`}
                                    onClick={() => this.setState({ category: c })}
                                    aria-selected={category === c}>
                                    {c === 'all' ? 'All' : c}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <colgroup>
                                <col className={styles.colMime} />
                                <col className={styles.colExt} />
                                <col className={styles.colDesc} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>MIME type</th>
                                    <th>Extensions</th>
                                    <th className={styles.colDesc}>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(m => (
                                    <tr key={m.mime}>
                                        <td className={styles.mimeCell}>{m.mime}</td>
                                        <td className={styles.extCell}>
                                            {m.extensions.length > 0 ? m.extensions.map(e => <code key={e}>.{e}</code>) : <span className={styles.muted}>—</span>}
                                        </td>
                                        <td className={styles.colDesc}>{m.name}</td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={3} className={styles.empty}>No MIME types match your filter.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className={sharedStyles.label} style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        Showing {rows.length} of {MIME.length} types.
                    </p>
                </div>
            </div>
        );
    }
}
